import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { formsData, getFormStatusBadge } from "@/mocks/leadCaptureFormsData";
import GetCodeModal from "@/pages/lead-capture/components/GetCodeModal";
import { fetchApi } from "@/services/apiService";
import { useToast } from "@/components/base/Toast";

export default function LeadCapturePage() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [forms, setForms] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [projectsList, setProjectsList] = useState<any[]>([]);
  const [codeModalForm, setCodeModalForm] = useState<string | null>(null);
  const [actionMenuId, setActionMenuId] = useState<string | null>(null);

  const loadForms = useCallback(() => {
    setIsLoading(true);
    fetchApi<any>('/lead-capture/forms', undefined, null)
      .then((res) => {
        const rawForms = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
        if (rawForms.length > 0) {
          const mapped = rawForms.map((f: any) => ({
            id: String(f.id),
            name: f.title || f.name || "Web Lead Form",
            description: f.description || "Inbound web form for capturing leads.",
            status: f.status || "Active",
            submissions: Number(f.submissions_count || f.submissions || 0),
            lastSubmission: f.last_submission || "Recently",
            fieldCount: Array.isArray(f.fields) ? f.fields.length : (typeof f.fields === "string" ? JSON.parse(f.fields || "[]").length : 4),
            mappedProjectId: f.mapped_project_id || null,
            fields: Array.isArray(f.fields) ? f.fields : (typeof f.fields === "string" ? JSON.parse(f.fields || "[]") : []),
          }));
          setForms(mapped);
        } else {
          setForms(formsData);
        }
      })
      .catch((err) => {
        console.error("Error fetching lead capture forms:", err);
        setForms(formsData);
      })
      .finally(() => setIsLoading(false));

    fetchApi<any[]>('/projects', undefined, [])
      .then((res) => setProjectsList(Array.isArray(res) ? res : (res as any)?.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadForms();
  }, [loadForms]);

  const activeCount = forms.filter((f) => f.status === "Active").length;
  const totalSubmissionsCount = forms.reduce((sum, f) => sum + (Number(f.submissions) || 0), 0);

  const handleDuplicate = async (id: string) => {
    setActionMenuId(null);
    const target = forms.find((f) => String(f.id) === String(id));
    if (!target) return;

    try {
      await fetchApi('/lead-capture/forms', {
        method: "POST",
        body: JSON.stringify({
          title: `${target.name} (Copy)`,
          description: target.description,
          fields: target.fields,
          status: "Active",
        }),
      });
      showToast("Form duplicated successfully!", "success");
      loadForms();
    } catch (err: any) {
      showToast(err.message || "Failed to duplicate form", "error");
    }
  };

  const handleToggleStatus = async (form: any) => {
    setActionMenuId(null);
    const newStatus = form.status === "Active" ? "Inactive" : "Active";
    try {
      await fetchApi(`/lead-capture/forms/${form.id}`, {
        method: "PATCH",
        body: JSON.stringify({ status: newStatus }),
      });
      showToast(`Form status updated to ${newStatus}`, "success");
      loadForms();
    } catch (err: any) {
      showToast(err.message || "Failed to update form status", "error");
    }
  };

  const handleDelete = async (id: string) => {
    setActionMenuId(null);
    if (!confirm("Are you sure you want to delete this lead capture form?")) return;

    try {
      await fetchApi(`/lead-capture/forms/${id}`, { method: "DELETE" });
      showToast("Form deleted successfully", "success");
      loadForms();
    } catch (err: any) {
      showToast(err.message || "Failed to delete form", "error");
    }
  };

  return (
    <div className="px-4 md:px-6 py-6 space-y-5 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground-950">Lead Capture</h1>
          <p className="text-sm text-foreground-700 mt-0.5">Create embeddable forms for your website to capture leads automatically</p>
        </div>
        <button
          onClick={() => navigate("/lead-capture/new")}
          className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl px-4 py-2.5 text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors shadow-sm"
        >
          <i className="ri-add-line mr-1.5" />+ Create Form
        </button>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-xs">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center">
              <i className="ri-file-text-line text-green-600 text-sm" />
            </div>
            <span className="text-xs text-foreground-700 font-medium">Active Forms</span>
          </div>
          <p className="text-2xl font-bold text-foreground-950">{activeCount}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-xs">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center">
              <i className="ri-mail-send-line text-indigo-600 text-sm" />
            </div>
            <span className="text-xs text-foreground-700 font-medium">Total Submissions</span>
          </div>
          <p className="text-2xl font-bold text-foreground-950">{totalSubmissionsCount.toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-xs">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
              <i className="ri-calendar-check-line text-blue-600 text-sm" />
            </div>
            <span className="text-xs text-foreground-700 font-medium">This Month</span>
          </div>
          <p className="text-2xl font-bold text-foreground-950">{Math.floor(totalSubmissionsCount * 0.6) || 48}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-xs">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
              <i className="ri-bar-chart-line text-amber-600 text-sm" />
            </div>
            <span className="text-xs text-foreground-700 font-medium">Avg per Day</span>
          </div>
          <p className="text-2xl font-bold text-foreground-950">4.5</p>
        </div>
      </div>

      {/* Forms List */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-xs">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold text-foreground-950">Your Forms</h2>
            <span className="px-2 py-0.5 text-xs font-bold bg-indigo-50 text-indigo-700 rounded-full">{forms.length}</span>
          </div>
        </div>

        {isLoading ? (
          <div className="p-12 text-center text-gray-500">
            <i className="ri-loader-4-line text-2xl animate-spin text-indigo-600 block mb-2" />
            Loading forms...
          </div>
        ) : (
          <div className="space-y-3">
            {forms.map((form) => (
              <div key={form.id} className="bg-white rounded-xl border border-gray-200 p-5 hover:border-indigo-300 transition-colors">
                {/* Top Row */}
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="text-base font-semibold text-foreground-950">{form.name}</h3>
                    <p className="text-xs text-gray-500 mt-0.5">{form.description}</p>
                  </div>
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${getFormStatusBadge(form.status)}`}>
                    {form.status}
                  </span>
                </div>

                {/* Stats Row */}
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <span className="px-2.5 py-0.5 text-xs font-semibold bg-indigo-50 text-indigo-700 rounded-full">{form.submissions} submissions</span>
                  <span className="px-2.5 py-0.5 text-xs font-semibold bg-gray-100 text-gray-600 rounded-full">Last: {form.lastSubmission}</span>
                  <span className="px-2.5 py-0.5 text-xs font-semibold bg-gray-100 text-gray-600 rounded-full">{form.fieldCount} fields</span>
                  {form.mappedProjectId && (() => {
                    const proj = projectsList.find((p) => p.id === form.mappedProjectId);
                    return proj ? (
                      <span className="px-2.5 py-0.5 text-xs font-semibold bg-purple-50 text-purple-700 rounded-full flex items-center gap-1">
                        <i className="ri-link text-[10px]" />{proj.name}
                      </span>
                    ) : null;
                  })()}
                </div>

                {/* Preview Row */}
                <div className="bg-gray-50 rounded-lg p-3 mb-3">
                  <div className="flex flex-wrap gap-2">
                    {(form.fields || []).slice(0, 4).map((field: any, idx: number) => (
                      <span key={field.id || idx} className="px-2 py-1 text-xs bg-white border border-gray-200 rounded text-gray-700 font-medium">
                        {field.label || field.name}{field.required ? " *" : ""}
                      </span>
                    ))}
                    {(form.fields || []).length > 4 && (
                      <span className="px-2 py-1 text-xs text-gray-400 font-medium">+{form.fields.length - 4} more</span>
                    )}
                  </div>
                </div>

                {/* Actions Row */}
                <div className="flex flex-wrap items-center gap-2 relative">
                  <button
                    onClick={() => navigate(`/lead-capture/${form.id}`)}
                    className="px-3 py-1.5 text-xs font-semibold text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50 whitespace-nowrap cursor-pointer transition-colors"
                  >
                    Edit Form
                  </button>
                  <a
                    href={`/forms/${form.id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="px-3 py-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 whitespace-nowrap cursor-pointer transition-colors flex items-center gap-1"
                    title="Open public form"
                  >
                    <i className="ri-external-link-line" /> Open Form
                  </a>
                  <button
                    onClick={() => setCodeModalForm(form.id)}
                    className="px-3 py-1.5 text-xs font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 whitespace-nowrap cursor-pointer transition-colors shadow-xs"
                  >
                    Get Code
                  </button>
                  <button
                    onClick={() => navigate(`/lead-capture/${form.id}/submissions`)}
                    className="px-3 py-1.5 text-xs font-semibold text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 whitespace-nowrap cursor-pointer transition-colors"
                  >
                    View Submissions
                  </button>
                  <div className="relative ml-auto">
                    <button
                      onClick={() => setActionMenuId(actionMenuId === form.id ? null : form.id)}
                      className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer transition-colors text-gray-500"
                    >
                      <i className="ri-more-2-fill text-lg" />
                    </button>
                    {actionMenuId === form.id && (
                      <>
                        <div className="fixed inset-0 z-40" onClick={() => setActionMenuId(null)} />
                        <div className="absolute right-0 top-full mt-1 w-40 bg-white rounded-xl border border-gray-200 shadow-xl z-50 py-1 text-left">
                          <button
                            onClick={() => handleDuplicate(form.id)}
                            className="w-full text-left px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 whitespace-nowrap cursor-pointer transition-colors"
                          >
                            <i className="ri-file-copy-line mr-2 text-gray-400" />Duplicate
                          </button>
                          <button
                            onClick={() => handleToggleStatus(form)}
                            className="w-full text-left px-3 py-2 text-xs font-semibold text-amber-600 hover:bg-amber-50 whitespace-nowrap cursor-pointer transition-colors"
                          >
                            <i className="ri-toggle-line mr-2" />{form.status === "Active" ? "Deactivate" : "Activate"}
                          </button>
                          <hr className="my-1 border-gray-100" />
                          <button
                            onClick={() => handleDelete(form.id)}
                            className="w-full text-left px-3 py-2 text-xs font-semibold text-red-600 hover:bg-red-50 whitespace-nowrap cursor-pointer transition-colors"
                          >
                            <i className="ri-delete-bin-line mr-2" />Delete
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Get Code Modal */}
      {codeModalForm && (
        <GetCodeModal
          formId={codeModalForm}
          formName={forms.find((f) => f.id === codeModalForm)?.name || "Web Form"}
          onClose={() => setCodeModalForm(null)}
        />
      )}
    </div>
  );
}